import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import Typography from "@mui/material/Typography";

import PendingCertificatesList from "../../components/PendingCertificateList";
import Confirm from "../../components/Confirm";
import DeclineCertificateDialog from "./DeclineCertificateDialog";
import { getPendingCertificates, resolvePending } from "../../api/certificates";
import { fireEvent } from "../../hooks/usePubsub";
import useCrew from "../../hooks/useCrew";
import { captureHandledException } from "../../services/sentry";

const getUserName = (item) => item.user?.name || "";
const getCertificateDescription = (item) => `(${(item.isCustom ? `cc_` : "") + item.courseId}) ${item.name}`;

// request() throws the Response for 400 and 409, and the bare status code for other failures.
const statusOf = (error) => (typeof error === "number" ? error : error?.status);

// 409: someone else resolved the certificate in the meantime (ngft-api #582).
const ALREADY_RESOLVED = 409;

const PendingCertificates = ({ match }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { crew } = useCrew();
  const { crewId } = match.params;
  const [pending, setPending] = useState(null);
  const [confirmItem, setConfirmItem] = useState(null);
  const [declining, setDeclining] = useState(false);
  const [declineError, setDeclineError] = useState(null);
  const [refresh, setRefresh] = useState(1);

  useEffect(() => {
    (async () => {
      const pendingCertificates = await getPendingCertificates(crewId, crew?.baseIds);
      setPending(pendingCertificates);
    })();
  }, [crewId, crew?.baseIds, refresh]);

  const onResolve = (item, status) => {
    setConfirmItem({
      item,
      status,
    });
  };

  const reloadList = () => {
    fireEvent("pending-changed");
    setRefresh((current) => current + 1);
  };

  const onConfirm = async (state) => {
    if (state) {
      const item = confirmItem.item;
      try {
        await resolvePending(crewId, item.id, confirmItem.status, item.isCustom);
      } catch (error) {
        if (statusOf(error) === ALREADY_RESOLVED) {
          enqueueSnackbar(t("pending_certificates.already_resolved"), { variant: "warning" });
        } else {
          captureHandledException(error, {
            feature: "pending-certificates",
            component: "PendingCertificates",
            action: "approve",
            crewId,
            certificateId: item.id,
          });
          enqueueSnackbar(t("pending_certificates.approve_failed"), { variant: "error" });
        }
      }
      // Reload either way: the row is gone after a success or a 409, and a failure may have been partial.
      reloadList();
    }
    setConfirmItem(null);
  };

  const onDecline = async (reason) => {
    const item = confirmItem.item;
    setDeclining(true);
    setDeclineError(null);
    try {
      await resolvePending(crewId, item.id, "decline", item.isCustom, reason);
      reloadList();
      setConfirmItem(null);
    } catch (error) {
      const status = statusOf(error);
      if (status === ALREADY_RESOLVED) {
        // Retrying cannot help: close, say why and drop the stale row.
        enqueueSnackbar(t("pending_certificates.already_resolved"), { variant: "warning" });
        reloadList();
        setConfirmItem(null);
      } else if (status === 400) {
        setDeclineError(t("pending_certificates.decline_rejected"));
      } else {
        captureHandledException(error, {
          feature: "pending-certificates",
          component: "PendingCertificates",
          action: "decline",
          crewId,
          certificateId: item.id,
        });
        setDeclineError(t("pending_certificates.decline_failed"));
      }
    } finally {
      setDeclining(false);
    }
  };

  const closeDecline = () => {
    setDeclineError(null);
    setConfirmItem(null);
  };

  const handleUpdate = () => {
    setRefresh(refresh + 1);
  };

  return (
    <>
      <Typography variant="h4">{t("pending_certificates.title")}</Typography>
      {pending && <PendingCertificatesList pending={pending} resolve={onResolve} onUpdate={handleUpdate} />}
      {confirmItem?.status === "decline" && (
        <DeclineCertificateDialog
          open
          certificate={getCertificateDescription(confirmItem.item)}
          user={getUserName(confirmItem.item)}
          saving={declining}
          error={declineError}
          onCancel={closeDecline}
          onDecline={onDecline}
        />
      )}
      {confirmItem?.status === "approve" && (
        <Confirm title={t("pending_certificates.confirmation_title")} onConfirm={onConfirm} confirmColor="success">
          {t("pending_certificates.confirm_approve", {
            certificate: getCertificateDescription(confirmItem.item),
            user: getUserName(confirmItem.item),
          })}
        </Confirm>
      )}
    </>
  );
};

export default PendingCertificates;
